import ArrowDownSVG from 'assets/icons/arrow-down-icon.svg';
import DoneIcon from 'assets/ver2/icons/done-icon-2.svg';
import { Fade } from 'helpers/animation';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import styled from 'styled-components';
import { paragraphMixin } from 'styles';
import { domUtils } from 'utils';

const accordionHeaderClassName = 'tracking-accordion-header-text';

const CustomTrackingAccordion = ({ isSuccess, data }) => {
  const { t } = useLang();

  const [current, setCurrent] = useState(-1);

  const accordionHeaderText = isSuccess
    ? 'label_successful_referrals'
    : 'label_pending_referrals';

  const handleAccordionItemOnClick = (index) => {
    if (current === index) {
      setCurrent(-1);
      return;
    }
    setCurrent(index);
  };

  const renderStatus = (options) => {
    if (isSuccess)
      return (
        <SuccessStatus>
          {t('label_referrals_status_1')}
          <img src={DoneIcon} width={20} height={20} />
        </SuccessStatus>
      );
    if (!isSuccess) return null;
    const { status, progress } = options || {};
    return (
      <StatusWrap>
        <ProgressBar now={progress} max={3} />
        <span>{status}</span>
      </StatusWrap>
    );
  };

  const renderAccordionItemBodyContent = (index) => {
    const isActive = current === index;
    if (isActive)
      return (
        <AccordionItemBodyContent $isActive={isActive}>
          {isSuccess ? (
            <>
              {data?.map((r, index) => (
                <RefereeSection key={index}>
                  <Text>{r}</Text>
                  {renderStatus()}
                </RefereeSection>
              ))}
            </>
          ) : (
            <>
              {data?.map((r, index) => (
                <RefereeSection key={index}>
                  <Text>{r}</Text>
                  {renderStatus({
                    status: t('label_referrals_status_2'),
                    progress: 1,
                  })}
                </RefereeSection>
              ))}
            </>
          )}
        </AccordionItemBodyContent>
      );
    return <></>;
  };

  if (isEmpty(data)) return null;
  return (
    <FieldWrap>
      <AccordionStyled>
        <AccordionItem
          eventKey={0}
          onClick={() => handleAccordionItemOnClick(0)}
        >
          <AccordionHeader
            $height={
              domUtils.isReady &&
              document.getElementsByClassName(accordionHeaderClassName)?.[0]
                ?.offsetHeight
            }
          >
            <AccordionHeaderText className={accordionHeaderClassName}>
              <StatusBadge $isSuccess={isSuccess}>
                <span>{data?.length}</span>
              </StatusBadge>
              {t(accordionHeaderText)}
            </AccordionHeaderText>
            <AccordionHeaderBody>
              <Divider $isActive={current !== -1} />
              {renderAccordionItemBodyContent(0)}
            </AccordionHeaderBody>
          </AccordionHeader>
        </AccordionItem>
      </AccordionStyled>
    </FieldWrap>
  );
};

const FieldWrap = styled.div`
  ${Fade};
  display: grid;
  width: 100%;
  height: fit-content;
  border-radius: 12px;
  border: 1px solid #7a8189;
  background-color: #fff;
`;

const AccordionStyled = styled(Accordion)`
  width: 100%;
  margin-top: 0px;
`;
const AccordionItem = styled(Accordion.Item)`
  border: none;
  background: transparent;
  border-radius: 0;
  margin-bottom: 0px;
  border-bottom: 1px solid #7a8189;
  width: calc(100% - 20px);
  margin-inline: 10px;

  &:last-child {
    border-bottom: none;
  }
`;
const AccordionHeader = styled(Accordion.Header)`
  height: fit-content;
  padding: 12px;
  padding-inline: 0px;
  box-shadow: none !important;
  border-radius: 12px;

  & button {
    padding: 0;
    background-color: transparent !important;
    box-shadow: none !important;
    display: block;

    &:focus,
    &:hover {
      z-index: 1;
    }

    &::after,
    &:not(.collapsed)::after {
      background-image: url(${ArrowDownSVG});
      background-position: center;
      background-size: 10px 6px;
      position: absolute;
      top: ${(props) => `${props.$height ? (props.$height - 20) / 2 : 0}px`};
      right: 0px;
    }
  }
`;
const AccordionHeaderText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);
  text-transform: uppercase;

  padding: 0;
  padding-right: 20px;
  margin: 0;
  min-height: 40px;
  display: flex;
  align-items: center;
`;

const AccordionHeaderBody = styled.div``;
const AccordionItemBodyContent = styled.div``;

const Divider = styled.div`
  border-bottom: 1px solid #ececf0;
  margin: 12px 0;
  margin-bottom: ${(props) => props.$isActive && 0};
  display: ${(props) => !props.$isActive && 'none'};
`;

const StatusBadge = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 5px;
  background: ${(props) =>
    props.$isSuccess
      ? 'linear-gradient(137.29deg, #1AA758 3.47%, #4AD888 107.8%)'
      : 'linear-gradient(137.29deg, #FCB519 3.47%, #FFDB8A 107.8%)'};

  & span {
    ${paragraphMixin};
    font-size: 24px;
    line-height: 30px;
    color: #fff;
  }
`;

const RefereeSection = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Text = styled.p`
  ${paragraphMixin};
  font-weight: 500;
  color: #000;
  font-size: 14px;
  line-height: 18px;
`;

const StatusWrap = styled.span`
  width: fit-content;
  height: 25px;
  display: grid;
  justify-items: end;
  gap: 2px;

  & span {
    ${paragraphMixin};
    font-weight: 500;
    font-size: 12px;
    line-height: 15px;
    color: #4965a0;
  }

  & div.progress {
    border: 1px solid #000;
    border-radius: 100px;
    height: 8px;
    width: 80px;
    background-color: #fff;
    & div.progress-bar {
      background-color: var(--ds-c-yellow);
    }
  }
`;

const SuccessStatus = styled.span`
  ${paragraphMixin};
  font-weight: 500;
  text-transform: uppercase;
  font-size: 12px;
  line-height: 15px;

  width: fit-content;
  height: 20px;
  border-radius: 100px;
  padding-inline: 20.5px;
  background: #ececf0;
  display: flex;
  align-items: center;
  gap: 2px;
`;

export default CustomTrackingAccordion;
